import { useCallback, useEffect, useMemo } from 'react';
import { TemplateElement } from '@bynder-studio/render-core';
import { useTranslate } from '@bynder/localization';
import useElement from 'packages/pages/design/hooks/useElement';
import useDesign from 'packages/pages/design/hooks/useDesign';
import useForceUpdate from '~/hooks/useForceUpdate';
import { ShotElementType } from 'packages/pages/design/sidebar/types';

type Props = {
    shotElement: TemplateElement;
    forceRender: boolean;
    isToolbarDisabled: boolean;
    isLoading: boolean;
    isDisabledByReview: boolean;
};

export const useShotItem = ({ shotElement, forceRender, isToolbarDisabled, isLoading, isDisabledByReview }: Props) => {
    const { translate } = useTranslate();
    const { id } = shotElement;
    const { element, updateElement } = useElement(id);
    const { assetLoader, creativeModel } = useDesign();
    const forceUpdate = useForceUpdate();

    const { hidden, locked, allowToggleVisibility } = element || {};
    const type = useMemo(() => shotElement.type.toLocaleLowerCase(), [shotElement.type]) as ShotElementType;
    const isTextElement = type === 'text';
    const isImageElement = type === 'image';
    const isShapeElement = type === 'shape';
    const isAudioElement = type === 'audio';

    const blockedByColorsLack = useMemo(() => {
        if (type === 'shape') {
            const { fillBrandColors, borderBrandColors } = shotElement.properties;

            const unappliedFillColors = fillBrandColors.filter(
                (brandColorId) => brandColorId !== shotElement.properties.fillColor.brandColorId,
            );
            const unappliedBorderColors = borderBrandColors.filter(
                (brandColorId) => brandColorId !== shotElement.properties.borderColor.brandColorId,
            );

            return !(unappliedFillColors.length + unappliedBorderColors.length);
        }

        return false;
    }, [shotElement]);

    const isDisabled = isToolbarDisabled || isLoading || isDisabledByReview;

    const isVisibleOnCanvas = allowToggleVisibility ? !hidden : true;
    const shotToBeHidden =
        (!allowToggleVisibility && locked) ||
        (!forceRender && !allowToggleVisibility && hidden) ||
        (!allowToggleVisibility && blockedByColorsLack);
    const isBaseContentVisible = (forceRender || isVisibleOnCanvas) && !locked && !blockedByColorsLack;
    const isContentProperty = !!element?.contentPropertyId;

    const getVisibilityButtonText = () => {
        if (!isAudioElement) {
            return isVisibleOnCanvas
                ? translate('design.sidebar.shots.element.hide')
                : translate('design.sidebar.shots.element.show');
        }

        return isVisibleOnCanvas
            ? translate('design.sidebar.shots.element.audio.mute')
            : translate('design.sidebar.shots.element.audio.unmute');
    };

    const toggleShow = useCallback(() => {
        if (!creativeModel) {
            return;
        }

        updateElement({ hidden: !hidden });
    }, [hidden, creativeModel, updateElement]);

    useEffect(() => {
        if (isTextElement || isShapeElement) {
            return;
        }

        assetLoader.eventEmitter.on('asset.load', forceUpdate);

        return () => {
            assetLoader.eventEmitter.off('asset.load', forceUpdate);
        };
    }, [assetLoader]);

    return {
        isTextElement,
        isImageElement,
        isShapeElement,
        isAudioElement,
        isDisabled,
        element,
        shotToBeHidden,
        isBaseContentVisible,
        allowToggleVisibility,
        getVisibilityButtonText,
        toggleShow,
        id,
        type,
        isVisibleOnCanvas,
        isContentProperty,
    };
};
